import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import BrightnessAutoRoundedIcon from "@mui/icons-material/BrightnessAutoRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useTheme } from "../../theme/ThemeContext";

/* ------------------------------------------------------------------
   Header "Appearance" menu (replaces the old dark-mode switch).

   Two independent choices, both saved on this browser by ThemeContext:
     Appearance — Light, Dark, or Auto (follows the computer's setting)
     Style      — Liquid Glass (translucent) or Classic (solid)
------------------------------------------------------------------ */

const MODES = [
  { value: "light", label: "Light", icon: LightModeRoundedIcon },
  { value: "dark", label: "Dark", icon: DarkModeRoundedIcon },
  { value: "system", label: "Auto", icon: BrightnessAutoRoundedIcon },
];

// Tiny swatch of each style so the choice is visual, not a word.
const StylePreview = ({ glass, dark }) => (
  <span
    aria-hidden="true"
    className="relative block h-14 w-full overflow-hidden rounded-xl border border-black/5 dark:border-white/10"
    style={{
      background: glass
        ? dark
          ? "radial-gradient(80% 90% at 0% 0%, rgba(14,165,233,.55), transparent 60%), radial-gradient(70% 80% at 100% 100%, rgba(139,92,246,.5), transparent 60%), #0b1020"
          : "radial-gradient(80% 90% at 0% 0%, rgba(125,211,252,.9), transparent 60%), radial-gradient(70% 80% at 100% 100%, rgba(196,181,253,.9), transparent 60%), #e8eef7"
        : dark
        ? "#121827"
        : "#eef2f7",
    }}
  >
    <span
      className="absolute inset-x-2 top-2 h-2.5 rounded-full"
      style={{
        background: glass
          ? dark
            ? "rgba(255,255,255,.14)"
            : "rgba(255,255,255,.6)"
          : dark
          ? "#1e293b"
          : "#ffffff",
        boxShadow: glass ? "inset 0 1px 0 rgba(255,255,255,.7)" : "none",
      }}
    />
    <span
      className="absolute bottom-2 left-2 right-8 top-6 rounded-md"
      style={{
        background: glass
          ? dark
            ? "rgba(30,41,59,.55)"
            : "rgba(255,255,255,.6)"
          : dark
          ? "#1e293b"
          : "#ffffff",
        border: glass
          ? `1px solid ${dark ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.85)"}`
          : `1px solid ${dark ? "#334155" : "#e2e8f0"}`,
      }}
    />
  </span>
);

const ThemeMenu = () => {
  const { mode, style, isDark, setTheme, setStyle } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const TriggerIcon = isDark ? DarkModeRoundedIcon : LightModeRoundedIcon;

  return (
    <div ref={rootRef} className="relative">
      <Tooltip title="Appearance">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Appearance settings"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="app-icon-btn"
        >
          <TriggerIcon fontSize="small" />
        </button>
      </Tooltip>

      {open && (
        <div
          role="dialog"
          aria-label="Appearance"
          className="app-glass app-glass-rim absolute right-0 top-12 z-50 w-[19rem] max-w-[calc(100vw-1.5rem)] rounded-2xl border p-3"
        >
          <p className="app-eyebrow px-1 pb-2">Appearance</p>
          <div
            role="radiogroup"
            aria-label="Appearance"
            className="grid grid-cols-3 gap-1 rounded-xl bg-[color:var(--app-hover)] p-1"
          >
            {MODES.map(({ value, label, icon: Icon }) => {
              const active = mode === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setTheme(value)}
                  className={`flex flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                    active
                      ? "bg-white text-slate-900 shadow-sm dark:bg-white/15 dark:text-white"
                      : "app-muted hover:text-[color:var(--app-text)]"
                  }`}
                >
                  <Icon sx={{ fontSize: 18 }} />
                  {label}
                </button>
              );
            })}
          </div>
          {mode === "system" && (
            <p className="app-muted px-1 pt-1.5 text-[11px]">
              Matches your computer's light or dark setting.
            </p>
          )}

          <p className="app-eyebrow px-1 pb-2 pt-4">Style</p>
          <div role="radiogroup" aria-label="Style" className="grid grid-cols-2 gap-2">
            {[
              { value: "glass", label: "Liquid Glass", hint: "Translucent" },
              { value: "classic", label: "Classic", hint: "Solid" },
            ].map((option) => {
              const active = style === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setStyle(option.value)}
                  className={`rounded-xl p-1.5 text-left transition ${
                    active
                      ? "ring-2 ring-sky-500"
                      : "ring-1 ring-black/5 hover:ring-black/15 dark:ring-white/10 dark:hover:ring-white/25"
                  }`}
                >
                  <StylePreview glass={option.value === "glass"} dark={isDark} />
                  <span className="mt-1.5 flex items-center justify-between px-0.5">
                    <span>
                      <span className="block text-xs font-semibold">
                        {option.label}
                      </span>
                      <span className="app-muted block text-[11px]">
                        {option.hint}
                      </span>
                    </span>
                    {active && (
                      <CheckRoundedIcon
                        sx={{ fontSize: 16 }}
                        className="text-sky-600 dark:text-sky-300"
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="app-muted px-1 pt-3 text-[11px]">
            Saved on this browser.
          </p>
        </div>
      )}
    </div>
  );
};

export default ThemeMenu;
